/**
 * CURRENCY-SHIM — мульти-валютная интеграция в калькулятор.
 *
 * Phase 8c (2026-05-07).
 * Подход: НЕ модифицируем formulы в public/js/app/calculations.js и НЕ
 * модифицируем updateCalculations() в public/js/app.js. Вместо этого:
 *
 *   1. Добавляем кеш курсов / комиссий / списка валют (`_currencyCache`).
 *   2. Wrap'аем агрегирующие методы (calculateBaseCost / calculateTotalProfit /
 *      calculateClientTotal / updateCalculations) — внутри подменяем
 *      `state.services / hotels / flights / otherServices` на копии, где
 *      `service.price` нормализован в USD (через convertWithFees), вызываем
 *      ОРИГИНАЛЬНЫЙ метод, восстанавливаем массивы. Так все формулы остаются
 *      нетронутыми и продолжают работать в долларах, а multi-currency
 *      становится прозрачным слоем «сверху».
 *   3. calculateServiceTotal(service) тоже wrap'ается (он принимает service
 *      параметром — конвертим его price перед вызовом original).
 *   4. updateCalculations() после originalCall() обновляет дополнительный
 *      breakdown под grand-total в client currency.
 *   5. Save/Load: в prepareEstimateData() и loadQuoteFromServer (через
 *      monkey-patch) сохраняется `clientCurrency` (default 'USD').
 *
 * Backward-compat:
 *   - service без `priceCurrency` → трактуется как USD.
 *   - state без `clientCurrency` → 'USD'.
 *   - если кэш не загружен (rates пустые) — формулы работают как раньше
 *     (все цены трактуются как USD, никаких конверсий).
 *
 * @module app/currency-shim
 */

// i18n helper: returns translation if loaded, else fallback (EN). Safe to call before module loads.
const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет multi-currency методы и оборачивает существующие расчёты.
 * Должно быть вызвано в entry.js ПОСЛЕ attachCalculations() и attachAutosave().
 *
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachCurrencyShim(ClassRef) {
    const proto = ClassRef.prototype;

    // ════════════════════════════════════════════════════════════════════
    // ▼ DOMAIN: CURRENCY — CACHE + HELPERS
    // ════════════════════════════════════════════════════════════════════

    /**
     * Загрузить актуальные currencies / rates / org_settings и сохранить
     * в `this._currencyCache`. Идемпотентно — можно вызвать многократно.
     * Вызывается на init() (после apiClient готов) и при событии
     * `currencies-updated` (диспатчится из settings.js при изменении).
     *
     * @returns {Promise<void>}
     */
    proto._refreshCurrencyCache = async function _refreshCurrencyCache() {
        if (!window.apiClient || typeof window.apiClient.listCurrencies !== 'function') {
            // apiClient ещё не готов — пропускаем тихо
            return;
        }
        try {
            const data = await window.apiClient.listCurrencies();
            const currencies = data.currencies || [];
            const ratesArray = data.rates || [];
            const orgSettings = data.org_settings || [];

            // rates: { [from_currency]: rate_to_usd }
            const rates = { USD: 1 };
            ratesArray.forEach(r => {
                if (r && r.from_currency && r.to_currency === 'USD') {
                    rates[r.from_currency] = Number(r.rate);
                }
            });

            // fees: { [currency_code]: conversion_fee_percent }
            const fees = { USD: 0 };
            orgSettings.forEach(s => {
                if (s && s.currency_code) {
                    fees[s.currency_code] = Number(s.conversion_fee_percent) || 0;
                }
            });

            // active currencies (для UI селектов)
            const activeCodes = new Set(
                orgSettings.filter(s => s && s.is_active).map(s => s.currency_code)
            );
            // USD всегда активна (фолбэк, даже если нет настроек)
            activeCodes.add('USD');
            const activeCurrencies = currencies
                .filter(c => activeCodes.has(c.code))
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

            // default client currency (одна на org)
            const defaultClient = (orgSettings.find(s => s && s.is_default_client) || {}).currency_code || 'USD';

            this._currencyCache = {
                currencies,        // master list (все)
                activeCurrencies,  // только активные для этой org (UI селекты)
                rates,             // { code: rate_to_usd }
                fees,              // { code: fee_percent }
                defaultClient,
                loadedAt: Date.now()
            };

            // Если в state ещё не был установлен clientCurrency — берём default из org
            if (this.state && (!this.state.clientCurrency || this.state.clientCurrency === 'USD')) {
                // Не перезатираем явно выбранный пользователем не-USD — но дефолтный USD
                // подменяем на org default (как правило это RUB для русских org).
                if (defaultClient && defaultClient !== 'USD') {
                    this.state.clientCurrency = this.state.clientCurrency || defaultClient;
                }
            }
            if (this.state && !this.state.clientCurrency) {
                this.state.clientCurrency = 'USD';
            }
            // Перепопулируем UI селект (новые активные валюты + текущее значение)
            const sel = document.getElementById('client-currency-select');
            if (sel) {
                const cur = this.state.clientCurrency || 'USD';
                const list = activeCurrencies.length ? activeCurrencies : [{ code: 'USD', symbol: '$' }];
                sel.innerHTML = list.map(c =>
                    `<option value="${c.code}" ${c.code === cur ? 'selected' : ''}>${c.code} (${c.symbol})</option>`
                ).join('');
            }

            // Перерисовка для обновления селектов в карточках услуг
            try {
                if (typeof this.renderServices === 'function') this.renderServices();
                if (typeof this.renderHotels === 'function') this.renderHotels();
                if (typeof this.renderFlights === 'function') this.renderFlights();
                if (typeof this.renderOtherServices === 'function') this.renderOtherServices();
            } catch (_) { /* ignore — render может ещё быть не готов */ }

            // Триггерим расчёт чтобы обновить breakdown
            try {
                if (typeof this.updateCalculations === 'function') this.updateCalculations();
            } catch (_) { /* ignore */ }

            console.log('[Currency] Cache refreshed:', {
                rates: Object.keys(rates).length,
                fees: Object.keys(fees).length,
                active: activeCurrencies.length,
                defaultClient
            });
        } catch (err) {
            console.warn('[Currency] Failed to refresh cache (non-fatal):', err.message);
            // Оставляем старый кеш если был — иначе пусто
            if (!this._currencyCache) {
                this._currencyCache = {
                    currencies: [], activeCurrencies: [],
                    rates: { USD: 1 }, fees: { USD: 0 },
                    defaultClient: 'USD', loadedAt: 0
                };
            }
        }
    };

    /**
     * Возвращает цену услуги, конвертированную в USD (с учётом fee на обмен).
     * Используется для отображения и для подмены при расчётах.
     *
     * @param {Object} service - объект услуги
     * @returns {number} цена в USD
     */
    proto.getServicePriceUSD = function getServicePriceUSD(service) {
        if (!service) return 0;
        const cur = service.priceCurrency || 'USD';
        const price = Number(service.price) || 0;
        if (cur === 'USD') return price;

        const cache = this._currencyCache;
        if (!cache || !cache.rates || !cache.rates[cur]) {
            // Нет курса → fallback: считаем как USD (не теряем данные)
            return price;
        }
        const utils = window.CurrencyUtils;
        if (!utils || typeof utils.convertWithFees !== 'function') return price;

        const { converted } = utils.convertWithFees(
            price, cur, 'USD', cache.rates, cache.fees
        );
        return converted;
    };

    /**
     * Конвертирует USD-сумму в client-currency (state.clientCurrency).
     * Если cache нет / клиентская валюта USD → возвращает amount как есть.
     *
     * @param {number} usdAmount
     * @returns {{ amount: number, code: string }}
     */
    proto.convertUsdToClient = function convertUsdToClient(usdAmount) {
        const code = (this.state && this.state.clientCurrency) || 'USD';
        const num = Number(usdAmount) || 0;
        if (code === 'USD') return { amount: num, code: 'USD' };

        const cache = this._currencyCache;
        if (!cache || !cache.rates || !cache.rates[code]) {
            return { amount: num, code: 'USD' };
        }
        const utils = window.CurrencyUtils;
        if (!utils || typeof utils.convertWithFees !== 'function') {
            return { amount: num, code: 'USD' };
        }

        const { converted } = utils.convertWithFees(
            num, 'USD', code, cache.rates, cache.fees
        );
        return { amount: converted, code };
    };

    /**
     * Форматирует USD-сумму как client-currency строку с символом.
     * Использует CurrencyUtils.formatCurrency, если кеш загружен.
     *
     * @param {number} usdAmount
     * @returns {string}
     */
    proto.formatInClientCurrency = function formatInClientCurrency(usdAmount) {
        const { amount, code } = this.convertUsdToClient(usdAmount);
        const cache = this._currencyCache;
        const utils = window.CurrencyUtils;
        if (utils && cache && cache.currencies && cache.currencies.length) {
            return utils.formatCurrency(amount, code, cache.currencies);
        }
        // fallback — без символа
        return `${code} ${Math.round(amount).toLocaleString('en-US')}`;
    };

    // ════════════════════════════════════════════════════════════════════
    // ▼ DOMAIN: CURRENCY — PRINT/КП helpers (Phase 8e)
    // ════════════════════════════════════════════════════════════════════

    /**
     * Конвертирует произвольную сумму из `fromCurrency` в client-currency
     * для печатной версии КП. Возвращает { value, code }.
     *
     * Backward-compat:
     *   - target == from → identity
     *   - кеш не загружен / нет курса → identity к fromCurrency (не теряем данные)
     *   - state.clientCurrency не задан → 'USD'
     *
     * @param {number} amount
     * @param {string} fromCurrency  default 'USD' (т.к. итоги после shim'а в USD)
     * @returns {{ value: number, code: string }}
     */
    proto.convertForPrint = function convertForPrint(amount, fromCurrency) {
        const targetCurrency = (this.state && this.state.clientCurrency) || 'USD';
        const from = fromCurrency || 'USD';
        const num = Number(amount) || 0;
        if (from === targetCurrency) return { value: num, code: targetCurrency };

        const cache = this._currencyCache;
        const utils = window.CurrencyUtils;
        if (!cache || !cache.rates || !utils || typeof utils.convertWithFees !== 'function') {
            // Нет инфраструктуры → fallback: возвращаем без конверсии (в исходной валюте)
            return { value: num, code: from };
        }

        // Если нет курса для одной из валют (и при этом не USD) — fallback
        const haveFrom = from === 'USD' || cache.rates[from] != null;
        const haveTo = targetCurrency === 'USD' || cache.rates[targetCurrency] != null;
        if (!haveFrom || !haveTo) {
            return { value: num, code: from };
        }

        try {
            const { converted } = utils.convertWithFees(
                num, from, targetCurrency, cache.rates, cache.fees || {}
            );
            return { value: converted, code: targetCurrency };
        } catch (err) {
            // Например, toRate=0 → fallback к исходной валюте
            return { value: num, code: from };
        }
    };

    /**
     * Форматирует сумму в client-currency для печатной КП.
     * Если CurrencyUtils.formatCurrency доступен и кеш загружен — использует его.
     * Иначе — fallback на округление + код валюты.
     *
     * @param {number} amount
     * @param {string} fromCurrency  default 'USD'
     * @returns {string}  e.g. "₽ 12 500.00" / "$ 1 234"
     */
    proto.formatPrintAmount = function formatPrintAmount(amount, fromCurrency) {
        const { value, code } = this.convertForPrint(amount, fromCurrency);
        const cache = this._currencyCache;
        const utils = window.CurrencyUtils;
        if (utils && typeof utils.formatCurrency === 'function'
            && cache && Array.isArray(cache.currencies) && cache.currencies.length) {
            return utils.formatCurrency(value, code, cache.currencies);
        }
        // Fallback: округляем (без знаков после запятой) + локализуем + код валюты
        // Используем символ из кэша если есть
        const c = (cache && Array.isArray(cache.currencies))
            ? cache.currencies.find(x => x && x.code === code)
            : null;
        const symbol = (c && c.symbol) || code;
        const decimals = (c && Number.isFinite(c.decimal_places)) ? c.decimal_places : 0;
        const num = Number(value);
        if (!Number.isFinite(num)) return `${symbol} —`;
        // Если decimals=0 — целое число (как раньше с Math.round + toLocaleString)
        if (decimals === 0) {
            return `${symbol} ${Math.round(num).toLocaleString('en-US')}`;
        }
        return `${symbol} ${num.toFixed(decimals)}`;
    };

    /**
     * Возвращает информацию о client-currency для шапки печатной КП.
     *
     * @returns {{ code: string, symbol: string, name: string }}
     */
    proto.getClientCurrencyInfo = function getClientCurrencyInfo() {
        const code = (this.state && this.state.clientCurrency) || 'USD';
        const cache = this._currencyCache;
        let info = null;
        if (cache && Array.isArray(cache.currencies)) {
            info = cache.currencies.find(x => x && x.code === code);
        }
        // Дефолты для USD если кэш ещё не загружен
        const defaults = {
            USD: { symbol: '$', name: _t('app.currency.usd_name', 'US Dollar') }
        };
        const dft = defaults[code] || {};
        return {
            code,
            symbol: (info && info.symbol) || dft.symbol || code,
            name: (info && (info.name_ru || info.name)) || dft.name || code
        };
    };

    // ════════════════════════════════════════════════════════════════════
    // ▼ DOMAIN: CURRENCY — WRAP CALCULATIONS (USD-pivot для агрегаторов)
    // ════════════════════════════════════════════════════════════════════

    /**
     * Выполнить fn() с временно подменёнными `state.services / hotels /
     * flights / otherServices` — каждая услуга получает USD-нормализованную
     * `price`. После выполнения fn() — массивы восстанавливаются (даже при
     * исключении). Это позволяет ОРИГИНАЛЬНЫМ методам calculations работать
     * с USD-ценами без изменения формул.
     *
     * @param {Function} fn
     * @returns {*} результат fn()
     */
    function _withUsdPrices(self, fn) {
        if (!self || !self.state) return fn();
        const arrays = ['services', 'hotels', 'flights', 'otherServices'];
        const originals = {};
        let didSwap = false;
        for (const arr of arrays) {
            const list = self.state[arr];
            if (!Array.isArray(list)) continue;
            originals[arr] = list;
            // Mapping: только услуги с НЕ-USD priceCurrency требуют конверсии
            self.state[arr] = list.map(svc => {
                if (!svc || !svc.priceCurrency || svc.priceCurrency === 'USD') return svc;
                const usdPrice = self.getServicePriceUSD(svc);
                // Не клонируем если цена не изменилась (rate отсутствует — fallback)
                if (usdPrice === svc.price) return svc;
                didSwap = true;
                return { ...svc, price: usdPrice, _origPriceCurrency: svc.priceCurrency };
            });
        }
        try {
            return fn();
        } finally {
            if (didSwap || true) {
                // Безусловное восстановление — даже если didSwap=false, мы заменяли ссылки
                for (const arr of arrays) {
                    if (originals[arr] !== undefined) self.state[arr] = originals[arr];
                }
            }
        }
    }

    // — Wrap calculateServiceTotal(service)
    const originalCalcServiceTotal = proto.calculateServiceTotal;
    if (typeof originalCalcServiceTotal === 'function') {
        proto.calculateServiceTotal = function calculateServiceTotal(service) {
            if (!service || !service.priceCurrency || service.priceCurrency === 'USD') {
                return originalCalcServiceTotal.call(this, service);
            }
            const usdPrice = this.getServicePriceUSD(service);
            const usdService = { ...service, price: usdPrice };
            return originalCalcServiceTotal.call(this, usdService);
        };
    }

    // — Wrap calculateBaseCost / calculateTotalProfit / calculateClientTotal
    ['calculateBaseCost', 'calculateTotalProfit', 'calculateClientTotal'].forEach(name => {
        const original = proto[name];
        if (typeof original !== 'function') return;
        proto[name] = function () {
            const args = arguments;
            const self = this;
            return _withUsdPrices(self, () => original.apply(self, args));
        };
    });

    // — Wrap updateCalculations (живёт в основном классе app.js)
    const originalUpdateCalcs = proto.updateCalculations;
    if (typeof originalUpdateCalcs === 'function') {
        proto.updateCalculations = function updateCalculations() {
            const self = this;
            const result = _withUsdPrices(self, () => originalUpdateCalcs.call(self));
            // После основного расчёта — обновляем мульти-валютный breakdown
            try {
                if (typeof self._renderClientCurrencyBreakdown === 'function') {
                    self._renderClientCurrencyBreakdown();
                }
            } catch (err) {
                console.warn('[Currency] breakdown render failed:', err.message);
            }
            return result;
        };
    }

    // ════════════════════════════════════════════════════════════════════
    // ▼ DOMAIN: CURRENCY — UI BREAKDOWN
    // ════════════════════════════════════════════════════════════════════

    /**
     * Рендерит дополнительный блок "≈ <сумма> <CUR>" под grand-total
     * правой панели. Скрывается если clientCurrency = USD или нет курса.
     */
    proto._renderClientCurrencyBreakdown = function _renderClientCurrencyBreakdown() {
        let container = document.getElementById('client-currency-breakdown');
        if (!container) {
            // Контейнер ещё не создан — создаём один раз и вставляем после grand-total
            const grandTotal = document.getElementById('grand-total');
            if (!grandTotal) return; // правая панель ещё не отрендерена
            const profitBox = grandTotal.closest('.profit-box');
            if (!profitBox) return;
            container = document.createElement('div');
            container.id = 'client-currency-breakdown';
            container.style.cssText = 'display:none;margin-top:6px;padding-top:6px;border-top:1px dashed var(--gray-200);font-size:11px;';
            profitBox.appendChild(container);
        }

        const code = (this.state && this.state.clientCurrency) || 'USD';
        if (code === 'USD') {
            container.style.display = 'none';
            container.innerHTML = '';
            return;
        }

        // Считаем USD-итог (через wrap'нутый calculateClientTotal)
        const usdTotal = this.calculateClientTotal();
        const { amount } = this.convertUsdToClient(usdTotal);
        const cache = this._currencyCache;
        const utils = window.CurrencyUtils;
        if (!utils || !cache || !cache.rates || !cache.rates[code]) {
            container.style.display = 'none';
            container.innerHTML = '';
            return;
        }
        const formatted = utils.formatCurrency(amount, code, cache.currencies);

        // fee info
        const feePercent = (cache.fees && cache.fees[code]) || 0;
        const feeNote = feePercent > 0
            ? ` <span style="color:#9ca3af;font-size:10px;">(+${feePercent}% ${_t('app.currency.exchange_fee', 'exchange fee')})</span>`
            : '';

        container.style.display = 'block';
        container.innerHTML = `
            <div style="display:flex;justify-content:space-between;align-items:center;color:var(--gray-600);">
                <span>≈ ${_t('app.currency.in_client_currency', 'In client currency:')}</span>
                <span style="font-weight:600;color:var(--gray-900);">${formatted}</span>
            </div>
            <div style="margin-top:2px;color:#9ca3af;font-size:10px;">${_t('app.currency.via_usd_pivot', 'via USD pivot')}${feeNote}</div>
        `;
    };

    // ════════════════════════════════════════════════════════════════════
    // ▼ DOMAIN: CURRENCY — UI helpers (для render-методов)
    // ════════════════════════════════════════════════════════════════════

    /**
     * HTML-снипет селекта валюты для одной услуги.
     * Использовать в render-методах рядом с input "Цена".
     *
     * @param {Object} service - сервис из state
     * @param {string} type - 'service' | 'hotel' | 'flight' | 'other'
     * @returns {string} HTML
     */
    proto._currencySelectHtml = function _currencySelectHtml(service, type) {
        const cache = this._currencyCache;
        const list = (cache && cache.activeCurrencies && cache.activeCurrencies.length)
            ? cache.activeCurrencies
            : [{ code: 'USD', symbol: '$' }];
        const current = service.priceCurrency || 'USD';
        const fieldClass = `${type}-field`;
        const opts = list.map(c =>
            `<option value="${c.code}" ${c.code === current ? 'selected' : ''}>${c.code}</option>`
        ).join('');
        return `<select class="input ${fieldClass} service-currency-select"
                       data-field="priceCurrency"
                       data-service-id="${service.id}"
                       data-service-type="${type}"
                       title="${_t('app.currency.service_price_currency', 'Service price currency')}"
                       style="width:60px;font-size:11px;padding:4px 4px;">
                    ${opts}
                </select>`;
    };
}
